import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { SuiNotification, SuiSpinner } from '@splashthat/sui';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useNotificationManager } from 'common/compounds/NotificationManager';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import useZoomOBFToken from 'common/compounds/VirtualEventRenderer/hooks/useZoomOBFToken';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';
import { useFlags } from 'utils/react-context';

import ZoomEmbedIframe from './ZoomEmbedIframe';

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
`;

function ZoomEmbed({ guest, meetingId, password, signature, features, onLeaveMeeting }) {
    const zoomIframeRef = useRef(null);
    const { showNotification } = useNotificationManager();
    const { search } = useLocation();
    const urlSearchParams = new URLSearchParams(search);
    const authCode = urlSearchParams.get('code');
    const { sessionId } = useParams();
    const { event } = useTouchpointData();
    const {
        data: obfTokenResponse,
        isLoading,
        isSuccess,
    } = useZoomOBFToken({ meetingId, code: authCode }, onLeaveMeeting);
    const obfToken = obfTokenResponse?.obf_token;
    const { guestAccessHash } = useGuestAccessHash(`zoomEmbed/${event.id}/guestAccessHash`);
    const { zoomSdkObfTokenEnabled } = useFlags();

    useEffect(() => {
        if (zoomIframeRef.current) {
            zoomIframeRef.current.addEventListener('load', () => {
                zoomIframeRef.current.contentWindow.document.addEventListener('zoom-error', ({ detail }) => {
                    const isResultMessageUnique =
                        typeof detail.result !== 'object' && detail.errorMessage !== detail.result;
                    const notificationMessage = `${detail.errorMessage} ${isResultMessageUnique ? detail.result : ''}`;

                    const notification = {
                        id: uniqueId('ZOOM_ERROR_'),
                        message: notificationMessage,
                        onClose: () => onLeaveMeeting({ meetingId }),
                        variation: SuiNotification.ERROR,
                    };

                    showNotification(notification);

                    if (detail.method === 'join') {
                        onLeaveMeeting({ meetingId });
                    }
                });
            });
        }
    }, [zoomIframeRef.current]);

    useEffect(() => {
        if (isSuccess && obfTokenResponse?.redirectUrl) {
            const url = new URL(obfTokenResponse.redirectUrl);
            url.searchParams.set('guest-access-hash', guestAccessHash);
            url.searchParams.set('meeting-id', meetingId);
            if (sessionId) {
                url.searchParams.set('session-id', sessionId);
            }
            window.location.assign(url.toString());
        }
    }, [isSuccess, obfTokenResponse?.redirectUrl, guestAccessHash, meetingId, sessionId]);

    return (
        <>
            {isLoading || (zoomSdkObfTokenEnabled && !obfToken) ? (
                <LoadingContainer>
                    <SuiSpinner loading size="large" />
                    <VisuallyHidden>loading</VisuallyHidden>
                </LoadingContainer>
            ) : (
                <ZoomEmbedIframe
                    guest={guest}
                    meetingId={meetingId}
                    signature={signature}
                    password={password}
                    features={features}
                    onLeaveMeeting={onLeaveMeeting}
                    iframeRef={zoomIframeRef}
                    obfToken={obfToken}
                />
            )}
        </>
    );
}

ZoomEmbed.propTypes = {
    meetingId: PropTypes.string.isRequired,
    onLeaveMeeting: PropTypes.func.isRequired,
    guest: PropTypes.object.isRequired,
    password: PropTypes.string.isRequired,
    signature: PropTypes.string.isRequired,
    features: PropTypes.object.isRequired,
};

export default ZoomEmbed;
